// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// FloatingAssistant (Phase 5) — the omnipresent "paperclip".
//
// One draggable, collapsible widget mounted once at the app root that exposes the
// whole thesis through existing pieces:
//   • T-501 shell: pill ↔ draggable panel, per-user persisted position.
//   • T-502 context: usePageContext defaults capture workflow + grounds converse.
//   • T-503 dispatch: Ask → usePrometheusAgent (agent self-routes); Log → the
//     hands-free pipeline; find-similar as a context action. No intent classifier.
//   • T-504 confirm gate: capture stages in hf_review_items → ReviewCard commit;
//     "N pending" badge on the collapsed pill.
//   • T-505 nudges (stretch): opt-in, grounded, dismissible suggestion.
//
// It never blocks the page (bounded fixed box, no overlay) and only renders for
// authenticated users.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "next-themes";
import {
  Bell,
  GripHorizontal,
  MessageSquare,
  Minus,
  Search,
  SquarePen,
} from "lucide-react";
import { PrometheanMark } from "@/components/PrometheanMark";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useFacility } from "@/contexts/FacilityContext";
import { usePageContext, buildContextPreamble } from "@/hooks/usePageContext";
import { HandsFreeEntry } from "@/components/hands-free/HandsFreeEntry";
import { WORKFLOW_LABELS } from "@/components/hands-free/types";
import { SimilarItemsDialog } from "@/components/prometheus-agent/SimilarItemsDialog";
import { AssistantConverse, type AssistantConverseHandle } from "./AssistantConverse";
import { useAssistantWindow } from "./useAssistantWindow";
import { usePendingReviewCount } from "./usePendingReviewCount";
import { useProactiveNudges } from "./useProactiveNudges";
import { STARTER_PROMPTS, contextStarterPrompts } from "@/lib/prometheusUi";

type Mode = "ask" | "log";

export function FloatingAssistant() {
  const { isAuthenticated, loading } = useAuth();
  const { activeFacility } = useFacility();
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();

  // Render the assistant in the opposite palette of the app (defaultTheme is
  // dark, so treat an unresolved theme as dark → assistant light). Force-light
  // uses the cp-theme-light token class; force-dark reuses the .dark class.
  const invertThemeClass = resolvedTheme === "light" ? "dark" : "cp-theme-light";

  const { open, setOpen, offset, isDragging, isMobile, onDragStart } = useAssistantWindow();
  const assistantSurface = resolvedTheme === "light" ? "dark" : "light";
  const ctx = usePageContext();
  const { count: pendingCount } = usePendingReviewCount();
  const { enabled: nudgesEnabled, setEnabled: setNudgesEnabled, nudge, dismiss } = useProactiveNudges();

  const [mode, setMode] = useState<Mode>("ask");
  const [similarOpen, setSimilarOpen] = useState(false);
  const converseRef = useRef<AssistantConverseHandle>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Default to Log mode on a page that implies a capture workflow.
  useEffect(() => {
    if (open && ctx.captureWorkflow) setMode("log");
  }, [open, ctx.captureWorkflow]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  const contextPreamble = useMemo(() => buildContextPreamble(ctx), [ctx]);

  const handsFreeContext = useMemo(() => {
    const pins: Record<string, string> = {};
    if (ctx.record?.table === "crops" && ctx.record.id) {
      pins.crop_id = ctx.record.id;
    }
    if (ctx.captureWorkflow) {
      pins.workflow = ctx.captureWorkflow;
    }
    return Object.keys(pins).length ? pins : undefined;
  }, [ctx.record, ctx.captureWorkflow]);

  const suggestions = useMemo(
    () => (ctx.record ? contextStarterPrompts(ctx.record.type) : STARTER_PROMPTS),
    [ctx.record],
  );

  if (loading || !isAuthenticated) return null;

  const askNudge = () => {
    if (!nudge) return;
    setOpen(true);
    setMode("ask");
    const question = nudge.question;
    dismiss();
    // Let the panel mount before asking.
    setTimeout(() => converseRef.current?.ask(question), 60);
  };

  // ── Collapsed pill ────────────────────────────────────────────────────────
  if (!open) {
    return (
      <div
        className={cn(
          invertThemeClass,
          "fixed z-[60] flex flex-col items-end gap-2 text-foreground",
          isMobile ? "right-2 bottom-[4.5rem]" : "",
        )}
        style={isMobile ? undefined : { right: offset.right, bottom: offset.bottom }}
      >
        {nudgesEnabled && nudge && (
          <div className="max-w-[18rem] rounded-lg border bg-background p-3 shadow-lg">
            <div className="flex items-start gap-2">
              <Bell className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <p className="text-xs text-left">{nudge.message}</p>
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={dismiss}>
                Dismiss
              </Button>
              <Button size="sm" className="h-7 px-2 text-xs" onClick={askNudge}>
                Show me
              </Button>
            </div>
          </div>
        )}
        <div className="relative">
          <Button
            type="button"
            size="icon"
            onClick={() => setOpen(true)}
            className="h-12 w-12 rounded-full shadow-lg"
            aria-label="Open Camp Promethean assistant"
          >
            <PrometheanMark surface={assistantSurface} className="h-6 w-6" title="Camp Promethean assistant" />
          </Button>
          {pendingCount > 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white"
              aria-label={`${pendingCount} items pending review`}
            >
              {pendingCount > 99 ? "99+" : pendingCount}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── Expanded panel ──────────────────────────────────────────────────────────
  return (
    <>
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Camp Promethean assistant"
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        className={cn(
          invertThemeClass,
          "fixed z-[60] flex flex-col overflow-hidden rounded-xl border bg-background text-foreground shadow-2xl outline-none",
          isMobile
            ? "inset-x-2 bottom-[4.5rem] max-h-[calc(100dvh-5.5rem)]"
            : "w-[380px] max-w-[calc(100vw-1rem)]",
        )}
        style={
          isMobile
            ? { height: "min(560px, calc(100dvh - 5.5rem))" }
            : {
                right: offset.right,
                bottom: offset.bottom,
                height: "560px",
                maxHeight: "calc(100vh - 1rem)",
              }
        }
      >
        {/* Header / drag handle (desktop only) */}
        <div
          onPointerDown={onDragStart}
          className={cn(
            "flex items-center gap-2 border-b bg-muted/40 px-3 py-2",
            !isMobile && (isDragging ? "cursor-grabbing" : "cursor-grab"),
          )}
        >
          {!isMobile && <GripHorizontal className="h-4 w-4 text-muted-foreground" />}
          <PrometheanMark surface={assistantSurface} className="h-4 w-4 shrink-0" />
          <span className="text-sm font-semibold">Prometheus</span>
          {ctx.facilityName && (
            <span className="truncate text-xs text-muted-foreground">· {ctx.facilityName}</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-7 w-7"
            onClick={() => setOpen(false)}
            aria-label="Minimize assistant"
          >
            <Minus className="h-4 w-4" />
          </Button>
        </div>

        {/* Mode toggle + context actions */}
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)} className="flex-1">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="ask" className="gap-1.5 text-xs">
                <MessageSquare className="h-3.5 w-3.5" /> Ask
              </TabsTrigger>
              <TabsTrigger value="log" className="gap-1.5 text-xs">
                <SquarePen className="h-3.5 w-3.5" /> Log
                {pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {ctx.record?.similarTable && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1 text-xs"
              onClick={() => setSimilarOpen(true)}
            >
              <Search className="h-3.5 w-3.5" /> Similar
            </Button>
          )}
        </div>

        {/* Content — both panes stay mounted so chat + capture state persist across toggles. */}
        <div className="min-h-0 flex-1 overflow-hidden">
          <div className={`h-full ${mode === "ask" ? "" : "hidden"}`}>
            <AssistantConverse ref={converseRef} contextPreamble={contextPreamble} suggestions={suggestions} />
          </div>
          <div className={`h-full overflow-y-auto p-3 ${mode === "log" ? "" : "hidden"}`}>
            {!activeFacility ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Select an active facility to log data.
              </p>
            ) : (
              <>
                {ctx.captureWorkflow && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    Logging to <span className="font-medium">{WORKFLOW_LABELS[ctx.captureWorkflow]}</span> for
                    this page. Review and confirm before it's saved.
                  </p>
                )}
                <HandsFreeEntry
                  workflow={ctx.captureWorkflow}
                  context={handsFreeContext}
                  onCommitted={() =>
                    toast({ title: "Saved", description: "Your log was committed." })
                  }
                />
              </>
            )}
          </div>
        </div>

        {/* Footer: pending + nudge control */}
        <div className="flex items-center justify-between gap-2 border-t bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
          <span>
            {pendingCount > 0 ? `${pendingCount} awaiting review` : "Nothing pending"}
          </span>
          <button
            type="button"
            className="flex items-center gap-1 hover:text-foreground"
            onClick={() => setNudgesEnabled(!nudgesEnabled)}
            aria-pressed={nudgesEnabled}
          >
            <Bell className={`h-3.5 w-3.5 ${nudgesEnabled ? "text-amber-500" : ""}`} />
            Nudges {nudgesEnabled ? "on" : "off"}
          </button>
        </div>
      </div>

      {ctx.record?.similarTable && ctx.record.id && (
        <SimilarItemsDialog
          open={similarOpen}
          onOpenChange={setSimilarOpen}
          sourceTable={ctx.record.similarTable}
          sourceId={ctx.record.id}
          title={`Similar to this ${ctx.record.type}`}
        />
      )}
    </>
  );
}

export default FloatingAssistant;
